import { Typography } from "@mui/material";
import { useFormikWithoutDirtyForm } from "@shared";
import { useConfirmationDialogContext } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

import { AttendanceType, appointmentsApi } from "@/api";
import { MultiDayAttendanceRequest, PlanMultiDayAttendanceRequest } from "@/api/api-client";
import { MultiDayFormValues } from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.types";
import { multiDayValidationSchema } from "@/common/components/appointments/plan-attendance/components/multi-day-form/MultiDayForm.utils";
import { useAttendancePurposes } from "@/common/components/appointments/plan-attendance/hooks/useAttendancePurposes";
import { deriveAvailability } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { SystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

type MultiDaySubmitValues = Omit<MultiDayFormValues, "dateFrom" | "dateTo" | "purposeKey" | "attendanceType"> & {
  dateFrom: Date;
  dateTo: Date;
  purposeKey: string;
  attendanceType: AttendanceType;
};

const INITIAL_VALUES: MultiDayFormValues = {
  dateFrom: null,
  dateTo: null,
  purposeKey: undefined,
  attendanceType: null,
  note: "",
  substituteCounselorId: null,
};

function buildRequest(values: MultiDaySubmitValues, purposes: SystemDropdownOptions): MultiDayAttendanceRequest {
  return {
    dateFrom: values.dateFrom,
    dateTo: values.dateTo,
    originTimeZoneId: getLocalTimeZoneId(),
    entry: {
      purposeKey: values.purposeKey,
      attendanceType: values.attendanceType,
      availability: deriveAvailability(values.purposeKey, purposes),
      details: values.note || null,
      substituteCounselorId: values.substituteCounselorId ?? null,
    },
  };
}

export function useMultiDayForm(onClose: () => void) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { showSuccess } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();
  const { purposes, purposeOptions, isLoading } = useAttendancePurposes();

  const planMultiDayMutation = useMutation({
    mutationFn: (request: PlanMultiDayAttendanceRequest) => appointmentsApi.planMultiDayAttendance(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });
      showSuccess(t("calendar.planAttendance.successMessage"));
      onClose();
    },
  });

  const formik = useFormikWithoutDirtyForm<MultiDayFormValues>({
    initialValues: INITIAL_VALUES,
    validationSchema: multiDayValidationSchema(t),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      const { dateFrom, dateTo, purposeKey, attendanceType } = values;
      if (!dateFrom || !dateTo || !purposeKey || attendanceType === null) {
        return;
      }

      const locale = locales[language];
      const formattedFrom = format(dateFrom, "dd.MM.yyyy", { locale });
      const formattedTo = format(dateTo, "dd.MM.yyyy", { locale });

      const confirmed = await new Promise<boolean>((resolve) => {
        showConfirmationDialog({
          dialogTitleText: t("calendar.planAttendance.multiDayConfirmTitle"),
          dialogText: (
            <Typography component="span">
              {t("calendar.planAttendance.multiDayConfirmTextBefore")}
              <Typography component="span" fontWeight={700}>
                {formattedFrom}
              </Typography>
              {t("calendar.planAttendance.multiDayConfirmTextBetween")}
              <Typography component="span" fontWeight={700}>
                {formattedTo}
              </Typography>
              {t("calendar.planAttendance.multiDayConfirmTextAfter")}
            </Typography>
          ),
          agreeText: t("calendar.planAttendance.multiDayConfirmAgree"),
          agreeColor: "primary",
          discardText: t("common.cancel"),
          onClose: resolve,
        });
      });

      if (confirmed) {
        await planMultiDayMutation.mutateAsync({
          multiDayAttendanceRequest: buildRequest(
            { ...values, dateFrom, dateTo, purposeKey, attendanceType },
            purposes
          ),
        });
      }
    },
  });

  return {
    formik,
    purposes,
    purposeOptions,
    isLoading,
  };
}
